import React, { useState } from "react";
import { IPatientInformation } from "../../../libs/data-access/types";
import { EButton, EModal, ESelect, ETextArea, ETextInput } from "../../../libs/ui";
import EDateInput from "../../../libs/ui/src/DateInput/EDateInput";
import moment from "moment";
import { useDirectusApi } from "../../../libs/data-access";
import { showToast } from "../../../utils/utility";

/* ================= TYPES ================= */

type OptionType = {
    label: string;
    value: string;
};

interface UpdateStatusModalState {
    last_followup_date: string | null;
    followup_type: string | null;
    vital_status: string | null;
    surgical_status: string | null;
    clinical_status: string | null;
    other_clinical_status: string | null;
    date_of_event: string | null;
    clinical_status_date: string | null;
    remarks: string | null
}

const options: {
    followUpType: OptionType[];
    presentVitalStatus: OptionType[];
    presentSurgicalStatus: OptionType[];
    presentClinicalStatus: OptionType[];
} = {
    followUpType: [
        { label: "Physical", value: "Physical" },
        { label: "Telephonic", value: "Telephonic" }
    ],
    presentVitalStatus: [
        { label: "Alive", value: "Alive" },
        { label: "Dead", value: "Dead" },
        { label: "Not known", value: "Not known" }
    ],
    presentSurgicalStatus: [
        { label: "Surgery done", value: "Surgery done" },
        { label: "Surgery scheduled", value: "Surgery scheduled" },
        { label: "Surgery not planned", value: "Surgery not planned" },
        { label: "Not known", value: "Not known" }
    ],
    presentClinicalStatus: [
        { label: "Asymptomatic", value: "Asymptomatic" },
        { label: "Biliary colic", value: "Biliary colic" },
        { label: "Acute cholecystitis", value: "Acute cholecystitis" },
        { label: "CBD stone without cholangitis/jaundice", value: "CBD stone without cholangitis/jaundice" },
        { label: "CBD stone with jaundice", value: "CBD stone with jaundice" },
        { label: "CBD stone with cholangitis", value: "CBD stone with cholangitis" },
        { label: "Acute pancreatitis", value: "Acute pancreatitis" },
        { label: "Carcinoma GB", value: "Carcinoma GB" },
        { label: "Not known", value: "Not known" },
        { label: "Others", value: "Others" }
    ]
};

/* ================= COMPONENT ================= */

const UpdateStatusModal = ({ patientDetails, onClose }: { patientDetails: IPatientInformation, onClose: (successTrue: boolean) => void }) => {

    const { patchDirectData } = useDirectusApi()

    const [isBtnLoading, setIsBtnLoading] = useState<boolean>(false)
    const [form, setForm] = useState<UpdateStatusModalState>({
        last_followup_date: patientDetails?.last_followup_date || null,
        followup_type: patientDetails?.followup_type || null,
        vital_status: patientDetails?.vital_status || null,
        surgical_status: patientDetails?.surgical_status || null,
        clinical_status: patientDetails?.clinical_status || null,
        date_of_event: patientDetails?.date_of_event || null,
        clinical_status_date: patientDetails?.clinical_status_date || null,
        other_clinical_status: patientDetails?.other_clinical_status || null,
        remarks: patientDetails?.remarks || null
    });

    const handleChange = (
        key: keyof UpdateStatusModalState,
        value: string | null
    ) => {
        setForm((prev) => ({
            ...prev,
            [key]: value
        }));
    };


    const handleUpdateCurrentStatus = async () => {
        setIsBtnLoading(true)
        const response = await patchDirectData(`items/patiant_profile/${patientDetails?.id}`, form);
        if (response?.isSuccess) {
            showToast(
                {
                    title: "Updated",
                    message: "Details updated Successfully"
                }
            )
            onClose(true)
        } else {
            showToast({
                message: response?.message || '',
                type: "error",
            })
        }
        setIsBtnLoading(false)
    }

    return (
        <EModal
            title={'Update Patient Current Status'}
            isOpen={true}
            size={"xl"}
            onClose={() => { onClose(false) }}
            setIsOpen={() => { onClose(false) }}
        >
            <div className="grid grid-cols-2 gap-3">

                <EDateInput
                    label="Last Follow Up"
                    value={form?.last_followup_date ? new Date(form?.last_followup_date) : undefined}
                    onChange={(e) => handleChange('last_followup_date', e ? moment(e).format('YYYY-MM-DD') : null)}
                />
                <div></div>
                <ESelect
                    label="Follow Up Type"
                    value={form?.followup_type}
                    onChange={(value) => handleChange('followup_type', value)}
                    data={options?.followUpType}
                />
                <ESelect
                    label="Vital Status"
                    value={form?.vital_status}
                    onChange={(value) => handleChange("vital_status", value)}
                    data={options?.presentVitalStatus}
                />
                <div className="flex flex-col gap-3">
                    <ESelect
                        label="Surgical Status"
                        value={form?.surgical_status}
                        onChange={(value) => {
                            handleChange("surgical_status", value)
                            if (value !== "Surgery done" && value !== "Surgery scheduled") {
                                handleChange("date_of_event", null)
                            }
                        }}
                        data={options?.presentSurgicalStatus}
                    />
                    {(form?.surgical_status === "Surgery done" || form?.surgical_status === "Surgery scheduled") && (
                        <EDateInput
                            label="Surgical Date"
                            value={form?.date_of_event ? new Date(form?.date_of_event) : undefined}
                            onChange={(e) => handleChange("date_of_event", e ? moment(e).format('YYYY-MM-DD') : null)}
                        />
                    )}
                </div>

                <div className="flex flex-col gap-3">
                    <ESelect
                        label="Clinical Status"
                        value={form?.clinical_status}
                        onChange={(value) => {
                            handleChange("clinical_status", value)
                            handleChange("other_clinical_status", null)
                            handleChange("clinical_status_date", null)
                        }}
                        data={options?.presentClinicalStatus}
                    />
                    {(form?.clinical_status?.length && form?.clinical_status !== 'Asymptomatic') && (
                        <EDateInput
                            label="Clinical Event Date"
                            value={form?.clinical_status_date ? new Date(form?.clinical_status_date) : undefined}
                            onChange={(e) => handleChange("clinical_status_date", e ? moment(e).format('YYYY-MM-DD') : null)}
                        />
                    )}
                </div>

                {form?.clinical_status === "Others" && (
                    <div className="col-span-2">
                        <ETextInput
                            label="Other Clinical Status"
                            value={form?.other_clinical_status || ""}
                            onChange={(e) => handleChange("other_clinical_status", e?.target?.value)}
                            placeholder="Describe"
                        />
                    </div>
                )}
                <div className="col-span-2">
                    <ETextArea
                        label="Remarks"
                        placeholder="Remarks"
                        value={form?.remarks || ""}
                        onChange={(e) => handleChange("remarks", e?.target?.value)}
                    />
                </div>
            </div>
            <div className="flex justify-end gap-2 items-center mt-6">
                <EButton
                    loading={isBtnLoading}
                    disabled={!patientDetails?.id}
                    onClick={() => {
                        handleUpdateCurrentStatus()
                    }}
                >
                    Update
                </EButton>
            </div>
        </EModal>
    );
};

export default UpdateStatusModal;